import { lazy, Suspense } from 'react';
import { Skeleton, Stack } from '@mui/material';

// Launch markets — controls which countries the picker offers.
// When more than one entry is listed the picker shows a real country
// dropdown; with a single entry it renders as a read-only field. Add
// ISO alpha-2 codes here to open a new market; the impl also needs an
// entry in COUNTRY_LABELS / REGIONS_BY_COUNTRY (LocationPickerImpl).
export const LAUNCH_COUNTRY_CODES = ['DE', 'FR', 'BE', 'SN'] as const;
export type LaunchCountryCode = (typeof LAUNCH_COUNTRY_CODES)[number];

/** Stable picker payload — what the parent form persists. */
export interface LocationValue {
  countryCode: string;     // ISO-3166-1 alpha-2, e.g. 'DE'
  countryName: string;     // human-readable, e.g. 'Deutschland'
  stateCode?: string;      // ISO sub-division code if a region was picked
  stateName?: string;      // localised Bundesland from Geoapify
  cityName?: string;
  latitude?: number;
  longitude?: number;
  /** Geoapify place_id — stored alongside coords so we can later
   *  reconcile / refresh a record without re-typing. Optional for
   *  legacy rows persisted before Geoapify integration. */
  placeId?: string;
}

export type LocationPickerProps = {
  value: LocationValue | null;
  onChange: (value: LocationValue) => void;
  /** Override the launch list (e.g. to expose every country for admin use). */
  allowedCountryCodes?: readonly string[];
  /** Show the State/Region row.  Default: true. */
  showState?: boolean;
  /** Compact horizontal layout when true; vertical otherwise. */
  horizontal?: boolean;
  /** All three fields can be marked required for forms. */
  required?: boolean;
};

// The heavy implementation imports `country-state-city`, whose city.json
// is ~8 MB. Parsing that on every page boot was overflowing iOS WebKit's
// JS stack (RangeError at :0:0 — boot stalled, #root empty) because the
// home-page route statically imports AccountSettingsPage / BecomeProvider,
// which both pull in this component. Lazy-loading keeps that dataset in
// its own chunk, fetched only when the picker is actually mounted.
const LocationPickerImpl = lazy(() => import('./LocationPickerImpl'));

export function LocationPicker(props: LocationPickerProps) {
  return (
    <Suspense
      fallback={
        <Stack spacing={2}>
          <Skeleton variant="rounded" height={56} />
          {props.showState !== false && (
            <Skeleton variant="rounded" height={56} />
          )}
          <Skeleton variant="rounded" height={56} />
        </Stack>
      }
    >
      <LocationPickerImpl {...props} />
    </Suspense>
  );
}
